import { EditorControl } from '../../../common/components/lexical';
import { OnLexicalError } from '../../../common/components/lexical/types';
import { useToast, VStack, Box, Select, Text } from '@cad-arc';
import { ConversationParticipantTypeEnum, ConversationParticipantView } from '@mark43/cad-api';
import React, { useCallback, useState, useLayoutEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { errorOccurredWhile } from '../../../common/helpers/error-occurred-while';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { findExistingConversationForMembers } from '../../helpers/conversation-helpers';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { useMessagingResource } from '../../hooks/resource/use-messaging-resource';
import { AddMessageInput } from '../add-message/add-message-input';
import { RecipientsSelect, RecipientsSelectOption } from './recipients-select';
import { ConversationKey } from '../../models';
import { observer } from 'mobx-react';
import { useAddMessage } from '../../hooks/resource/use-add-message';
import { SendAsOption } from './types';
import {
    convertParticipantViewToSendAsOption,
    convertSendAsOptionToMemberView,
    convertSendAsOptionToParticipantView,
    getSendAsOptions,
    getUniqueMembers,
} from './helpers';
import { buildParticipantKey } from '../../store/helpers';
import { useApplicationType } from '../../../common/hooks/use-application-type';

export interface NewConversationFormProps {
    onLexicalError?: OnLexicalError;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
}

type NewConversationFormType = {
    recipients: RecipientsSelectOption[];
    sendAs: SendAsOption | null;
    addMessage: string;
};

export const NewConversationForm = observer(
    ({ editorControlRef }: NewConversationFormProps): React.JSX.Element => {
        const {
            addConversation,
            conversations,
            isAddUrgentMessageEnabled,
            selectedConversationFilter,
            setSelectedConversationKey,
            setSelectedConversationFilter,
            participantIdentities,
        } = useMessagingStore();
        const { sendMessagesAsIndividual } = useEnabledFeatures();
        const { addMessage, addCreateConversationMessage } = useAddMessage();
        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const { createConversation } = useMessagingResource();
        const toast = useToast();
        const { isFirstResponder } = useApplicationType();
        const { filterType } = selectedConversationFilter;

        const sendAsOptions = getSendAsOptions(
            participantIdentities?.activeMappedByType,
            sendMessagesAsIndividual
        );
        let sendAsSelected = sendMessagesAsIndividual
            ? participantIdentities?.activeMappedByType.USER
            : undefined;
        // if open new conversation from tabs below, set default 'send as' to corresponding role
        if (Object.keys(ConversationParticipantTypeEnum).includes(filterType)) {
            sendAsSelected = selectedConversationFilter?.filterParticipant?.id
                ? participantIdentities?.activeMappedByKey[
                      buildParticipantKey(
                          selectedConversationFilter.filterParticipant.id,
                          selectedConversationFilter.filterParticipant.entityType
                      )
                  ]
                : undefined;
        }
        const defaultSendAs = sendAsSelected
            ? convertParticipantViewToSendAsOption(sendAsSelected)
            : undefined;
        const [sendAsParticipantIdentity, setSendAsParticipantIdentity] = useState<
            ConversationParticipantView | undefined
        >(sendAsSelected);

        const {
            control,
            reset,
            setFocus,
            handleSubmit,
            trigger,
            formState: { errors },
            clearErrors,
        } = useForm<NewConversationFormType>({
            mode: 'onChange',
            defaultValues: { sendAs: defaultSendAs, recipients: [], addMessage: '' },
        });

        const isSendAsInvalid = !!errors.sendAs?.message;

        const validateSendAs = useCallback(
            (option: SendAsOption | null) => {
                if (!option) {
                    return;
                }
                // 'sendAsParticipantIdentity' from useState is not synced. Use real time value from option for validation.
                const isSendAsUserEntityTypeSync = option?.entityType === 'USER';
                if (isSendAsUserEntityTypeSync && !sendMessagesAsIndividual) {
                    return 'Cannot send messages as an individual user';
                }

                const isSendAsStillActive =
                    !!participantIdentities?.activeMappedByKey[
                        buildParticipantKey(option.value, option.entityType)
                    ];
                if (!isSendAsStillActive) {
                    return `Cannot send as inactive identity (${option.label})`;
                }

                return;
            },
            [sendMessagesAsIndividual, participantIdentities?.activeMappedByKey]
        );

        useLayoutEffect(() => {
            setTimeout(() => {
                if (!sendAsSelected) {
                    setFocus('sendAs');
                } else {
                    setFocus('recipients');
                }
            }, 0);
        }, [setFocus, sendAsSelected]);

        const onSubmit: SubmitHandler<NewConversationFormType> = async (form) => {
            try {
                setIsSubmitting(true);
                editorControlRef.current?.setEditable(false);
                const isValid = await trigger();
                if (!isValid) {
                    setIsSubmitting(false);
                    return;
                }

                const sendAsOption = form.sendAs;
                if (!sendAsOption) {
                    return;
                }
                const filterParticipant =
                    participantIdentities?.activeMappedByKey[
                        buildParticipantKey(sendAsOption.value, sendAsOption.entityType)
                    ];
                if (!filterParticipant) {
                    return;
                }

                const text = form['addMessage'];

                const senderMember = convertSendAsOptionToMemberView(sendAsOption);
                const senderParticipant = convertSendAsOptionToParticipantView(sendAsOption);
                const recipientMembers = form.recipients.map(({ recipient }) => recipient);

                const members = getUniqueMembers([senderMember, ...recipientMembers]);

                const redirectFilterType = sendAsOption.entityType;
                const filter = {
                    filterType: redirectFilterType,
                    filterParticipant,
                };

                let conversationKey: ConversationKey;
                const existingConversation = findExistingConversationForMembers(
                    members,
                    conversations
                );
                if (existingConversation) {
                    conversationKey = new ConversationKey(
                        existingConversation.id,
                        senderParticipant
                    );
                    await addMessage({
                        isUrgent: isAddUrgentMessageEnabled,
                        senderParticipant,
                        text,
                        conversationKey,
                    });
                } else {
                    const createConversationResponse = await createConversation({
                        members,
                        message: {
                            isUrgent: isAddUrgentMessageEnabled,
                            senderParticipant,
                            text,
                        },
                    });
                    const { conversation, message } = createConversationResponse ?? {};
                    if (conversation === undefined) {
                        throw new Error('conversation not created');
                    }
                    if (message === undefined) {
                        throw new Error(
                            'initial message is not created during conversation creation'
                        );
                    }
                    conversationKey = new ConversationKey(conversation.id, senderParticipant);
                    addCreateConversationMessage(message, conversationKey);
                    addConversation(conversation);
                }
                reset();
                editorControlRef.current?.clear();
                setSelectedConversationFilter(filter);
                setSelectedConversationKey(conversationKey);
            } catch (err) {
                editorControlRef.current?.focus();
                toast({
                    status: 'error',
                    description: errorOccurredWhile('creating a conversation'),
                });
            } finally {
                setIsSubmitting(false);
                editorControlRef.current?.setEditable(true);
            }
        };

        return (
            <form
                onSubmit={handleSubmit(onSubmit)}
                style={{ width: '100%', height: '100%', marginTop: 0 }}
            >
                <VStack position="absolute" justifyContent="space-between" w="100%" h="100%">
                    <VStack
                        alignItems="start"
                        w="100%"
                        bg="var(--arc-colors-surface-foreground)"
                        pt={isFirstResponder ? 14 : 8}
                        pb={4}
                        px={4}
                    >
                        <Text variant="headingSm">Send As</Text>
                        <Controller
                            name="sendAs"
                            control={control}
                            defaultValue={defaultSendAs}
                            rules={{
                                required: { value: true, message: 'Required' },
                                validate: validateSendAs,
                            }}
                            render={({ field }) => (
                                // eslint-disable-next-line react/forbid-elements
                                <Select
                                    {...field}
                                    data-testid="send-as-select"
                                    isInvalid={isSendAsInvalid}
                                    highlightResults={false}
                                    showSelectedGroup={false}
                                    onChange={(selected) => {
                                        clearErrors();
                                        const selectedOption =
                                            selected.detail.selectedOptionsData[0];
                                        if (selectedOption?.value) {
                                            setSendAsParticipantIdentity(
                                                convertSendAsOptionToParticipantView(selectedOption)
                                            );
                                            field.onChange(selectedOption);
                                        }
                                    }}
                                    options={sendAsOptions}
                                />
                            )}
                        />
                        {errors.sendAs?.message && (
                            <Text variant="bodyMd" color="negative">
                                {errors.sendAs.message}
                            </Text>
                        )}
                        <Text variant="headingSm">Recipient(s)</Text>
                        <Controller
                            name="recipients"
                            control={control}
                            defaultValue={[]}
                            rules={{
                                required: { value: true, message: 'Required' },
                                validate: () => trigger('sendAs'),
                            }}
                            render={({ field }) => (
                                <RecipientsSelect
                                    {...field}
                                    onChange={(updatedSelectedOptions) => {
                                        clearErrors();
                                        if (updatedSelectedOptions) {
                                            // onChange may be triggered by adding or removing selection to/from current selected options
                                            // updatedSelectedOptions always contain all currently selected options.
                                            field.onChange(updatedSelectedOptions);
                                        }
                                    }}
                                    data-testid="recipients-select"
                                />
                            )}
                        />
                        {errors['recipients']?.message && (
                            <Text variant="bodyMd" color="negative">
                                {errors['recipients'].message}
                            </Text>
                        )}
                    </VStack>
                    <Box w="100%" p="0 var(--arc-space-3) var(--arc-space-3) var(--arc-space-3)">
                        <AddMessageInput
                            control={control}
                            name="addMessage"
                            data-testid="add-message-input"
                            editorControlRef={editorControlRef}
                            isSubmitting={isSubmitting}
                            onSubmit={handleSubmit(onSubmit)}
                            sendAsParticipantIdentity={sendAsParticipantIdentity}
                        />
                    </Box>
                </VStack>
                <input type="submit" hidden />
            </form>
        );
    }
);
